import { CurrencyPipe, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Paginator } from 'primeng/paginator';
import { ViewChild } from '@angular/core';
import { MerchantPayoutReportService } from 'src/app/_business-dashboard/_services/merchant-payout-report.service';
import { DemoMerchantPayoutResponse, DemoMerchantPayoutSummaryResponse } from 'src/app/_data/merchant-payout-report';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { FilterServiceService } from 'src/app/_services/filter-service.service';
import { GlobalService } from 'src/app/_services/global.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-bizz-merchant-payout-report-list',
  templateUrl: './bizz-merchant-payout-report-list.component.html',
  styleUrls: ['./bizz-merchant-payout-report-list.component.scss']
})
export class BizzMerchantPayoutReportListComponent implements OnInit {

  _globals = GlobalService;

  page = 0;
  rows = 10;
  first = 0;
  totalRecords = 0;
  dataList: any[] = null;
  orderParam = '';
  orderBy = 'asc';

  summary: any = {
    totalSales: 0,
    totalOrders: 0,
    totalPlatformFeesCollected: 0,
    totalNetPayout: 0
  };

  /** Set when the user clicks a customer name/id to drill into just that customer's orders. */
  activeCustomerFilter: { id?: string; name?: string } = null;

  colHeader = this._abd.reports.filter(x => x.paraReportName == 'Merchant Payout Report')[0].summaryHeader;
  colflds = this._abd.reports.filter(x => x.paraReportName == 'Merchant Payout Report')[0].colflds;

  @ViewChild('p', { static: false }) paginator: Paginator;

  constructor(
    public _abd: AppBaseDataService,
    public _filterService: FilterServiceService,
    private currencyPipe: CurrencyPipe,
    private _reportsService: MerchantPayoutReportService,
  ) { }

  ngOnInit(): void {
    this._globals.componentName = 'Reports';
    this._globals.secondTitle = 'Merchant Payouts';
  }

  ngAfterViewInit() {
    let ti = null;
    ti = setInterval(() => {
      const filter = this._filterService.getFilters(this._globals.componentName);
      if (filter) {
        if (filter.filterValue) this._filterService.searchText = filter.filterValue;
      }
      clearInterval(ti);
      this.getDataList();
      this.getSummary();
    }, 500);
  }

  ngOnDestroy() {
    this._globals.componentName = '';
    this._globals.secondTitle = '';
  }

  private buildFilterModel() {
    return {
      filterValue: this._filterService.searchText,
      // Sent separately so the backend can do an exact-match lookup instead of a fuzzy name search
      // when the user drilled in via "View all orders" on a specific customer.
      customerId: this.activeCustomerFilter?.id ?? null,
      startDate: formatDate(this._filterService.dateRange[0], 'yyyy-MM-dd', 'en-US'),
      endDate: formatDate(this._filterService.dateRange[1], 'yyyy-MM-dd', 'en-US'),
      orderParam: this.orderParam,
      orderBy: this.orderBy,
    };
  }

  /** Single-click drill-in: show only this customer's orders, by id when available, else by exact name. */
  filterByCustomer(item: any) {
    this.activeCustomerFilter = { id: item.customerId, name: item.customerName };
    this._filterService.searchText = item.customerName;
    this.page = 0;
    this.getDataList();
    this.getSummary();
  }

  clearCustomerFilter() {
    this.activeCustomerFilter = null;
    this._filterService.searchText = '';
    this.page = 0;
    this.getDataList();
    this.getSummary();
  }

  /** Detail view (per-order, with Order #/Date) only once the user has drilled into one customer. */
  get isDetailView(): boolean {
    return !!this.activeCustomerFilter;
  }

  /** Main view: one row per customer, fees/payout summed across all their orders, no Order #/Date. */
  get aggregatedByCustomer(): any[] {
    if (!this.dataList?.length) return [];
    const byCustomer = new Map<string, any>();
    this.dataList.forEach((row: any) => {
      const key = row.customerId || row.customerName;
      if (!byCustomer.has(key)) {
        byCustomer.set(key, {
          customerId: row.customerId,
          customerName: row.customerName,
          orderCount: 0,
          totalAmountPaid: 0,
          paymentGatewayFee: 0,
          platformFee: 0,
          refundAmount: 0,
          netPayout: 0,
        });
      }
      const agg = byCustomer.get(key);
      agg.orderCount += 1;
      agg.totalAmountPaid += row.totalAmountPaid || 0;
      agg.paymentGatewayFee += row.paymentGatewayFee || 0;
      agg.platformFee += row.platformFee || 0;
      agg.refundAmount += row.refundAmount || 0;
      agg.netPayout += row.netPayout || 0;
    });
    return Array.from(byCustomer.values());
  }

  get displayList(): any[] {
    return this.isDetailView ? (this.dataList || []) : this.aggregatedByCustomer;
  }

  getDataList() {
    this._abd.showSpinner();
    const obj = this.buildFilterModel();

    this._reportsService.getMerchantPayoutReportList(obj).subscribe({
      next: (res: any) => {
        this._abd.hideSpinner();
        if (res?.success) {
          this.totalRecords = res.totalRecords;
          this.dataList = res.data;
        } else {
          this.handleListErrorFallback();
        }
      },
      error: () => {
        this.handleListErrorFallback();
      }
    });

    this._filterService.updateFilters(this._globals.componentName, obj);
    this._filterService.updatePages(this._globals.componentName, { page: this.page, rows: this.rows });
  }

  getSummary() {
    const obj = this.buildFilterModel();
    this._reportsService.getMerchantPayoutReportSummary(obj).subscribe({
      next: (res: any) => {
        if (res?.success) {
          this.summary = res.data;
        } else {
          this.handleSummaryErrorFallback();
        }
      },
      error: () => {
        this.handleSummaryErrorFallback();
      }
    });
  }

  private handleListErrorFallback() {
    this._abd.hideSpinner();
    if (!environment.production) {
      // No real backend to filter for us in dev, so apply the same customer-drill-in / search-text
      // filtering client-side against the mock dataset to keep the feature testable.
      const customerId = this.activeCustomerFilter?.id;
      const search = (this._filterService.searchText || '').trim().toLowerCase();
      const filtered = (DemoMerchantPayoutResponse.data || []).filter((row: any) => {
        if (customerId) return row.customerId === customerId;
        if (search) return String(row.customerName || '').toLowerCase().includes(search);
        return true;
      });
      this.dataList = filtered;
      this.totalRecords = filtered.length;
    } else {
      this.dataList = [];
      this.totalRecords = 0;
    }
  }

  private handleSummaryErrorFallback() {
    if (!environment.production) {
      this.summary = DemoMerchantPayoutSummaryResponse.data;
    } else {
      this.summary = { totalSales: 0, totalOrders: 0, totalPlatformFeesCollected: 0, totalNetPayout: 0 };
    }
  }

  refrestDataList() {
    this.getDataList();
    this.getSummary();
  }

  callbackFuncFromFilter(value: any) {
    this.getDataList();
    this.getSummary();
  }

  filterButtonPressed() {
    this.getDataList();
    this.getSummary();
  }

  onPageChange(e) {
    this.page = e.page;
    this.rows = e.rows;
    this.first = e.first;
    this.getDataList();
  }

  customSort(e) {
    const last = this.orderParam;
    switch (e.sortField) {
      case 'orderNumber':
        this.orderParam = 'orderNumber';
        break;
      case 'customerName':
        this.orderParam = 'customerName';
        break;
      case 'orderDate':
        this.orderParam = 'orderDate';
        break;
      case 'totalAmountPaid':
        this.orderParam = 'totalAmountPaid';
        break;
      case 'netPayout':
        this.orderParam = 'netPayout';
        break;
      default:
        this.orderParam = '';
    }
    if (this.orderParam !== '') {
      this.orderBy = e.sortOrder == 1 ? 'asc' : 'desc';
      if (last !== this.orderParam) this.orderBy = 'asc';
      this.getDataList();
    }
  }

  calculateSummaryTotal(col: string) {
    if (!this.displayList?.length) return this.currencyPipe.transform(0, 'USD', '$');
    const total = this.displayList.reduce((sum, item) => sum + (item[col] || 0), 0);
    return this.currencyPipe.transform(total, 'USD', '$');
  }

  exportExcel() {
    this._abd.exportExcel(this.dataList, this.colHeader[0], this.colflds, 'Merchant Payout Report');
  }

  exportPdf() {
    this._abd.exportPdf(this.dataList, this.colHeader, this.colflds, 'Merchant Payout Report', true, false, false);
  }
}
